import { useMemo } from "react";
import { useIntl } from "react-intl";
import { IconSend, IconShoppingCartPlus } from "@tabler/icons-react";
import { COLORES_ACCION_COTIZACION, MENSAJES, SELECCION } from "../constants/gestionCotizacion.constants";
import type {
  AccionCotizacion,
  Cotizacion,
  ProveedorCotizacion,
  Recotizacion,
} from "../types/gestionCotizacion.types";
import {
  agruparPorProveedor,
  esCotizacionComputo,
  esCotizacionEnviada,
  esDetalleAutorizadoDisponible,
  hayProductosRechazados,
  obtenerIdsMarcados,
  obtenerInventariosAutorizados,
  obtenerInventariosSinDisponibilidad,
  obtenerProductosPendientes,
  obtenerRechazadosSinProveedor,
} from "../utils/gestionCotizacion.utils";

interface Params {
  cotizacion: Cotizacion | null;
  procesando: boolean;
  idsDetalle: number[];
  recotizacion: Recotizacion;
  onEnviarAutorizar: (idsMarcados: number[]) => void;
  onRecotizar: () => void;
  onCrearOrdenes: (proveedores: ProveedorCotizacion[]) => void;
}

export const useAccionesCotizacion = ({
  cotizacion,
  procesando,
  idsDetalle,
  recotizacion,
  onEnviarAutorizar,
  onRecotizar,
  onCrearOrdenes,
}: Params) => {
  const intl = useIntl();

  const esComputo = esCotizacionComputo(cotizacion);
  const totalProductos = cotizacion?.productos_requisicion?.length ?? 0;

  const inventariosAutorizados = useMemo(
    () => obtenerInventariosAutorizados(cotizacion),
    [cotizacion]
  );

  const productosPendientes = useMemo(
    () => obtenerProductosPendientes(cotizacion, inventariosAutorizados),
    [cotizacion, inventariosAutorizados]
  );

  const idsMarcados = useMemo(() => obtenerIdsMarcados(cotizacion), [cotizacion]);

  const proveedoresConAutorizados = useMemo(
    () => agruparPorProveedor(cotizacion, esDetalleAutorizadoDisponible),
    [cotizacion]
  );

  const proveedoresSeleccionados = useMemo(
    () =>
      agruparPorProveedor(
        cotizacion,
        (d) =>
          idsDetalle.includes(Number(d.id)) || Number(d.seleccionado) === SELECCION.MARCADO
      ),
    [cotizacion, idsDetalle]
  );

  const inventariosSinDisponibilidad = useMemo(
    () => obtenerInventariosSinDisponibilidad(cotizacion),
    [cotizacion]
  );

  const hayRechazados = esComputo && hayProductosRechazados(cotizacion);
  const hayRecotizables = hayRechazados || inventariosSinDisponibilidad.size > 0;

  const rechazadosSinProveedor = useMemo(
    () => (esComputo ? obtenerRechazadosSinProveedor(cotizacion, recotizacion) : []),
    [esComputo, cotizacion, recotizacion]
  );

  const todosAutorizados =
    esComputo && totalProductos > 0 && inventariosAutorizados.size === totalProductos;
  const todosMarcados = totalProductos > 0 && productosPendientes.length === 0;
  const yaEnviada = esCotizacionEnviada(cotizacion);
  const ordenesGeneradas = Boolean(cotizacion?.tiene_ordenes_compra);

  const acciones = useMemo<AccionCotizacion[]>(() => {
    if (!cotizacion) return [];

    const accionOrden = (
      proveedores: ProveedorCotizacion[],
      bloqueado: boolean,
      faltan = 0
    ): AccionCotizacion => {
      const deshabilitado = bloqueado || ordenesGeneradas;
      return {
        ...COLORES_ACCION_COTIZACION,
        title: ordenesGeneradas
          ? "Órdenes de compra generadas"
          : procesando
            ? `${intl.formatMessage({ id: "general_generando" })}...`
            : `${intl.formatMessage({ id: "general_generar_orden_compra" })}${faltan > 0 ? ` (faltan ${faltan})` : ""
            }`,
        disabledButton: deshabilitado,
        icon: <IconShoppingCartPlus stroke={2} />,
        handleAction: () => {
          if (!deshabilitado) onCrearOrdenes(proveedores);
        },
      };
    };

    /**
     * Recotizar: rechazados por DG (cómputo) y/o sin disponibilidad (ambos tipos).
     * Solo los rechazados de cómputo son obligatorios ("faltan N");
     * los sin disponibilidad son opcionales.
     */
    const accionRecotizar = (): AccionCotizacion => {
      const faltan = rechazadosSinProveedor.length;
      const cantidad = Object.keys(recotizacion).length;
      const bloqueado = procesando || ordenesGeneradas || faltan > 0 || cantidad === 0;

      return {
        ...COLORES_ACCION_COTIZACION,
        title: procesando
          ? `${intl.formatMessage({ id: "general_enviando" })}...`
          : faltan > 0
            ? `Enviar a recotizar (faltan ${faltan})`
            : `Enviar a recotizar (${cantidad})`,
        disabledButton: bloqueado,
        icon: <IconSend stroke={2} />,
        handleAction: () => {
          if (!bloqueado) onRecotizar();
        },
      };
    };

    // Cotización normal: recotizar (si aplica) y generar orden con lo seleccionado
    if (!esComputo) {
      return [
        ...(hayRecotizables ? [accionRecotizar()] : []),
        accionOrden(
          proveedoresSeleccionados,
          procesando || !todosMarcados || proveedoresSeleccionados.length === 0,
          productosPendientes.length
        ),
      ];
    }

    // Cotización de cómputo
    const lista: AccionCotizacion[] = [];

    const bloqueadoEnviar = procesando || !todosMarcados || yaEnviada || todosAutorizados;
    lista.push({
      ...COLORES_ACCION_COTIZACION,
      title: procesando
        ? `${intl.formatMessage({ id: "general_enviando" })}...`
        : todosAutorizados
          ? MENSAJES.todosAutorizados
          : `${intl.formatMessage({ id: "general_mandar_autorizar_computo" })}${!todosMarcados ? ` (faltan ${productosPendientes.length})` : ""
          }`,
      disabledButton: bloqueadoEnviar,
      icon: <IconSend stroke={2} />,
      handleAction: () => {
        if (!bloqueadoEnviar) onEnviarAutorizar(idsMarcados);
      },
    });

    if (hayRecotizables) {
      lista.push(accionRecotizar());
    }

    if (todosAutorizados) {
      lista.push(
        accionOrden(proveedoresConAutorizados, procesando || proveedoresConAutorizados.length === 0)
      );
    }

    return lista;
  }, [
    cotizacion,
    procesando,
    esComputo,
    proveedoresSeleccionados,
    proveedoresConAutorizados,
    todosMarcados,
    todosAutorizados,
    yaEnviada,
    ordenesGeneradas,
    productosPendientes.length,
    idsMarcados,
    hayRecotizables,
    rechazadosSinProveedor.length,
    recotizacion,
    intl,
    onEnviarAutorizar,
    onRecotizar,
    onCrearOrdenes,
  ]);

  return {
    acciones,
    esComputo,
    inventariosAutorizados,
    inventariosSinDisponibilidad,
    productosPendientes,
    idsMarcados,
    hayRechazados,
    hayRecotizables,
    todosAutorizados,
    todosMarcados,
  };
};
